import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '../utils';

interface SliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  ThumbProps?: React.ComponentPropsWithoutRef<typeof SliderPrimitive.Thumb>;
  TrackProps?: React.ComponentPropsWithoutRef<typeof SliderPrimitive.Track>;
  RangeProps?: React.ComponentPropsWithoutRef<typeof SliderPrimitive.Range>;
  WrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  withThumbValue?: boolean;
  withEdgeValues?: boolean;
  minIcon?: React.ReactNode;
  maxIcon?: React.ReactNode;
  valueFormatter?: (value?: number) => string;
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      value,
      withThumbValue = false,
      withEdgeValues = false,
      valueFormatter = (value?: number) => value?.toString() || '',
      min,
      max,
      minIcon,
      maxIcon,
      ThumbProps,
      WrapperProps,
      TrackProps,
      RangeProps,
      defaultValue,
      ...props
    },
    ref
  ) => (
    <div
      {...WrapperProps}
      className={cn('flex items-start gap-1', WrapperProps?.className)}
    >
      {minIcon}
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          'relative flex w-full touch-none items-center select-none',
          className
        )}
        min={min}
        max={max}
        value={value}
        {...props}
      >
        <SliderPrimitive.Track
          {...TrackProps}
          className={cn(
            'bg-primary/35 relative h-2 w-full grow overflow-hidden rounded-full',
            TrackProps?.className
          )}
        >
          <SliderPrimitive.Range
            {...RangeProps}
            className={cn('bg-primary absolute h-full', RangeProps?.className)}
          />
        </SliderPrimitive.Track>
        {withEdgeValues && (
          <div className="absolute top-full mt-1.5 flex w-full justify-between text-xs">
            <span>{valueFormatter(min)}</span>
            <span>{valueFormatter(max)}</span>
          </div>
        )}

        {(value ?? []).map(() => (
          <SliderPrimitive.Thumb
            {...ThumbProps}
            className={cn(
              'border-primary bg-primary block h-5 w-5 rounded-full border-2 transition-colors disabled:pointer-events-none disabled:opacity-50',
              ThumbProps?.className
            )}
          />
        ))}
        {withThumbValue && (
          <span
            className={cn(
              'absolute top-full z-50 mt-[7px] -translate-x-[90%] text-xs'
            )}
            style={{
              left: `${valueFormatter(
                (defaultValue?.[0] ?? min ?? 0) / ((max ?? 100) + (min ?? 0))
              )}%`,
            }}
          >
            {valueFormatter(defaultValue?.[0])}
          </span>
        )}
      </SliderPrimitive.Root>
      {maxIcon}
    </div>
  )
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
